/** Local calendar date as YYYY-MM-DD (matches game store logic). */
export function formatLocalDateString (date) {
  const d = date instanceof Date ? date : new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function isScheduleDateInRange (schedule, dateStr) {
  if (schedule?.startDate && dateStr < schedule.startDate) return false
  if (schedule?.endDate && dateStr > schedule.endDate) return false
  return true
}

/** Resolved skate time for one date, or null if this schedule does not run that day. */
export function resolveScheduleTimeForDate (schedule, dateStr) {
  if (!schedule || !dateStr) return null
  if (!isScheduleDateInRange(schedule, dateStr)) return null

  const mode = schedule.scheduleMode || 'weekly'
  const sessionTimes = schedule.sessionTimes || {}

  if (mode === 'dated') {
    return sessionTimes[dateStr] || null
  }

  const scheduleDay = Number(schedule.day ?? schedule.dayOfWeek)
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  if (Number.isNaN(dt.getTime()) || dt.getDay() !== scheduleDay) return null

  return sessionTimes[dateStr] || schedule.time || null
}

/**
 * Next upcoming occurrence for schedule cards / admin sync.
 * Returns { date, time } or null when nothing upcoming.
 */
export function resolveNextScheduleOccurrence (
  schedule,
  {
    fromDate = new Date(),
    horizonDays = 60,
    isCancelled = () => false
  }: { fromDate?: Date; horizonDays?: number; isCancelled?: (dateStr: string) => boolean } = {}
) {
  if (!schedule) return null

  const mode = schedule.scheduleMode || 'weekly'
  const sessionTimes = schedule.sessionTimes || {}
  const todayStr = formatLocalDateString(fromDate)

  if (mode === 'dated') {
    for (const dateStr of Object.keys(sessionTimes).sort()) {
      if (dateStr < todayStr) continue
      if (!isScheduleDateInRange(schedule, dateStr)) continue
      if (isCancelled(dateStr)) continue
      const time = sessionTimes[dateStr]
      if (time) return { date: dateStr, time }
    }
    return null
  }

  for (let i = 0; i < horizonDays; i++) {
    const check = new Date(fromDate)
    check.setDate(fromDate.getDate() + i)
    const dateStr = formatLocalDateString(check)
    if (!isScheduleDateInRange(schedule, dateStr)) continue
    if (isCancelled(dateStr)) continue
    const time = resolveScheduleTimeForDate(schedule, dateStr)
    if (time) return { date: dateStr, time }
  }

  if (schedule.time) {
    return { date: null, time: schedule.time }
  }
  return null
}

export function formatScheduleNextDateLabel (dateStr) {
  if (!dateStr) return ''
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function scheduleShowsNextDate (schedule, next) {
  if (!next?.date) return false
  const mode = schedule?.scheduleMode || 'weekly'
  if (mode === 'dated') return true
  const sessionTimes = schedule?.sessionTimes || {}
  return Object.prototype.hasOwnProperty.call(sessionTimes, next.date)
}
